/**
 * 어드민 약관 API.
 *
 * 약관은 종류(5종)마다 버전 단위로 쌓인다. 현재 버전은 `effectiveAt <= now` 중 최신이고,
 * 시행 예정 버전은 종류당 1개만 존재할 수 있다(이미 있으면 등록 대신 수정).
 * 수정은 시행 예정·시행 중·지난 버전 모두 가능하고, 삭제는 동의 이력이 없는 버전만 가능하다.
 * 일시는 요청·응답 모두 KST 기준 `yyyy-MM-dd HH:mm:ss` 문자열이며 타임존 오프셋을 붙이지 않는다.
 */
import { createApiPath } from '@/shared/api/endpoints';
import { apiClient } from '@/shared/api/http-client';

export type TermsType =
  | 'TERMS_OF_SERVICE'
  | 'PRIVACY_COLLECTION_CONSENT'
  | 'COMPLIANCE_PLEDGE'
  | 'MARKETING_CONSENT'
  | 'PRIVACY_POLICY';

/** 목록에 실리는 버전 요약 — 현재/시행 예정 각각 */
export interface TermsVersionSummary {
  termsId: number;
  version: number;
  title: string;
  effectiveAt: string;
}

export interface TermsTypeListItem {
  type: TermsType;
  typeName: string;
  /** 필수 동의 여부 — false면 선택 약관 */
  required: boolean;
  /** 동의 대상 여부 — false면 게시만 하고 동의를 받지 않는다(개인정보 처리방침) */
  agreementTarget: boolean;
  current: TermsVersionSummary | null;
  scheduled: TermsVersionSummary | null;
}

export interface TermsDetail {
  termsId: number;
  type: TermsType;
  typeName: string;
  version: number;
  title: string;
  content: string;
  effectiveAt: string;
  changeSummary: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string | null;
  /** 시행 여부 — 시행일시 ≤ 현재면 true. 시행된 버전 수정 시 동의 본문 변경 안내에 쓴다 */
  effective: boolean;
  /** @deprecated 모든 버전을 수정할 수 있어 항상 true. 서버 호환용으로만 남아 있다 */
  editable: boolean;
}

export interface TermsVersionHistoryItem {
  termsId: number;
  version: number;
  effectiveAt: string;
  /** 다음 버전의 시행일시. 마지막(최신) 버전이면 null */
  endedAt: string | null;
  changeSummary: string | null;
  createdByName: string | null;
}

export interface TermsCreateRequest {
  type: TermsType;
  title: string;
  content: string;
  effectiveAt: string;
  changeSummary?: string;
}

/** 수정은 부분 수정이 아니라 전체 교체이며 type은 바꿀 수 없다 */
export interface TermsUpdateRequest {
  title: string;
  content: string;
  effectiveAt: string;
  changeSummary?: string;
}

export interface TermsCreateResponse {
  termsId: number;
  version: number;
}

export const termsService = {
  getTypes: async (): Promise<TermsTypeListItem[]> => {
    const response = await apiClient.get(createApiPath.terms.types());
    return response.data;
  },

  getDetail: async (termsId: number): Promise<TermsDetail> => {
    const response = await apiClient.get(createApiPath.terms.detail(termsId));
    return response.data;
  },

  getVersions: async (type: TermsType): Promise<TermsVersionHistoryItem[]> => {
    const response = await apiClient.get(createApiPath.terms.versions(type));
    return response.data;
  },

  create: async (data: TermsCreateRequest): Promise<TermsCreateResponse> => {
    const response = await apiClient.post(createApiPath.terms.types(), data);
    return response.data;
  },

  update: async (termsId: number, data: TermsUpdateRequest): Promise<void> => {
    await apiClient.put(createApiPath.terms.detail(termsId), data);
  },

  /**
   * 버전 삭제(204).
   * 동의 이력·진행 중인 SNS 가입이 있으면 409 `TERMS_IN_USE`,
   * 그 종류의 유일한 시행 버전이면 409 `LAST_EFFECTIVE_TERMS`.
   */
  delete: async (termsId: number): Promise<void> => {
    await apiClient.delete(createApiPath.terms.detail(termsId));
  },
};
